<template>
  <NuxtLink :aria-label="label" :to="{ path: link }" class="cta-card" exact>
    <div :class="{ 'no-bg': noMedia }" class="content">
      <h2 class="whitespace-pre-line">
        {{ title }}
      </h2>
      <div
        :class="[computedTheme.theme, noMedia ? 'white-border' : '']"
        class="button button--xl"
      >
        <span>
          {{ ctaLabel }}
        </span>
        <i aria-hidden="true" class="icon">
          <Icon name="fa6-solid:arrow-right" />
        </i>
      </div>
    </div>
    <MediaCardImage
      v-if="!noMedia"
      :max-width="480"
      :media="media"
      :quality="80"
      class="media--card"
    />
  </NuxtLink>
</template>

<script setup lang="ts">
import type { BlockCardsStoryblok } from '~/types/storyblok'

const props = withDefaults(
  defineProps<{
    blok?: BlockCardsStoryblok
    ctaLink?: string
    ctaLabel?: string
    cardTitle?: string
    noMedia?: boolean
  }>(),
  {
    blok: () => null,
    ctaLink: '',
    ctaLabel: 'Learn more',
    cardTitle: '',
    noMedia: false,
  },
)
const { fetchTopics } = useStoryblokDataSources()
const { data: topicTags } = await fetchTopics()

const storyContent = computed(() => {
  return props.blok?.link?.story?.content
})
const topics = computed(() => props.blok?.content?.topics || [])
const computedTheme = computed(() => {
  if (!topicTags) {
    return { theme: getTheme(1) }
  }

  return (
    topicTags.value
      .filter((topic) => {
        return topics.value.includes(topic.value)
      })
      .map((topic) => ({
        theme: getTheme(topic.color || 1),
      }))[0] || { theme: getTheme(1) }
  )
})
const title = computed(() => {
  return (
    props.cardTitle ||
    props.blok?.title ||
    props.blok?.label ||
    props.blok?.link?.story?.name ||
    props.blok?.link?.url ||
    props.blok?.link?.cached_url
  )
})
const { graphicImage, pageBanner, fallbackImage } = useStoryContent({
  ...toRefs(props),
  storyContent,
})
const label = computed(() => title.value?.replace(/[\r\n]/gm, ' '))
const link = computed(
  () => props.ctaLink || props.blok?.link?.url || props.blok?.link?.cached_url,
)
const cardFallbackImage = computed(() => {
  const filename = storyContent.value?.seo?.og_image
  if (!filename) return null
  return {
    filename,
  }
})
const media = computed(
  () =>
    (graphicImage.value?.filename && graphicImage) ||
    (pageBanner.value?.image?.filename && pageBanner.value?.image) ||
    cardFallbackImage.value ||
    fallbackImage,
)
const getTheme = (val: number) => 'theme-' + val
</script>

<style lang="less" scoped>
a.cta-card {
  .block();
  .relative();

  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: @color-secondary;
  transition: transform 0.3s ease-in-out;

  @media @desktop {
    &:hover {
      transform: translateY(-@9px);
    }
  }

  > * {
    width: 100%;
    height: 100%;
  }

  .content {
    .flex();
    .absolute();
    .flex-col();
    .justify-between();
    .align-start();

    z-index: 10;
    padding: @gutter-lg @gutter;

    &.no-bg {
      background: none;
    }

    h2 {
      .h1();

      max-width: 15ch;
      color: @color-body;

      @media @tablet {
        .font-2xl();
        .font-black();
        .space(0);
      }

      @media @wide {
        .font-3xl();
        .font-black();
        .space(0);
      }
    }

    .button {
      & .icon {
        color: @color-text-reverse;
      }

      &.white-border {
        border-color: @color-text-reverse;
      }
    }
  }
  &.small .content {
    h2 {
      .h2();
    }
    .button {
      background: none;
      border: none;
      padding: 0;

      span {
        .hidden();
      }

      i.icon {
        .font-2xl();

        padding: 0;
      }
    }
  }
  .media {
    .relative();

    &::before {
      .block();
      .absolute();

      content: '';
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      z-index: 5;
      background:
        linear-gradient(
          180deg,
          rgba(44, 57, 63, 0.66) 5%,
          rgba(44, 57, 63, 0) 33%
        ),
        rgba(90, 55, 150, 0.24);
      mix-blend-mode: multiply;
    }
  }
}
</style>
